import { useMemo } from 'react';
import { replaceUrlParams } from '@utils/string-util';
import type {
  OrganizationSiteProfileDomainRequest,
  OrganizationSiteProfileHeroImageRequest,
  OrganizationSiteProfileLogoImageRequest,
  OrganizationSiteProfileMenus,
  OrganizationSiteProfileRequest,
  OrganizationSiteProfileResponse,
  OrganizationSiteSocialsRequest,
  SiteDomainAvailabilityRequest,
  SiteDomainAvailabilityResponse,
} from 'types/organization-site';

import endpoints from '../../services/api/endpoints';
import useFetch from '../../services/api/useFetch';

export const useOrganizationSiteApi = () => {
  const api = useFetch();
  return useMemo(() => {
    return {
      async checkDomainAvailability(
        organizationId: string,
        params: SiteDomainAvailabilityRequest
      ): Promise<SiteDomainAvailabilityResponse> {
        return await api.get(
          replaceUrlParams(endpoints.sites.checkDomainAvailability, {
            organizationId,
          }),
          params
        );
      },
      async get(
        organizationId: string
      ): Promise<OrganizationSiteProfileResponse> {
        return await api.get(
          replaceUrlParams(endpoints.sites.get, { organizationId })
        );
      },
      async getMenus(
        organizationId: string
      ): Promise<OrganizationSiteProfileMenus> {
        return await api.get(
          replaceUrlParams(endpoints.sites.getMenus, { organizationId })
        );
      },
      async publish(
        organizationId: string
      ): Promise<OrganizationSiteProfileResponse> {
        return await api.post(
          replaceUrlParams(endpoints.sites.publish, { organizationId })
        );
      },
      async removeHeroImage(
        organizationId: string
      ): Promise<OrganizationSiteProfileResponse> {
        return await api.delete(
          replaceUrlParams(endpoints.sites.removeHeroImage, { organizationId })
        );
      },
      async removeLogoImage(
        organizationId: string
      ): Promise<OrganizationSiteProfileResponse> {
        return await api.delete(
          replaceUrlParams(endpoints.sites.removeLogoImage, { organizationId })
        );
      },
      async unpublish(
        organizationId: string
      ): Promise<OrganizationSiteProfileResponse> {
        return await api.post(
          replaceUrlParams(endpoints.sites.unpublish, { organizationId })
        );
      },
      async update(
        organizationId: string,
        params: OrganizationSiteProfileRequest
      ): Promise<OrganizationSiteProfileResponse> {
        return await api.post(
          replaceUrlParams(endpoints.sites.update, { organizationId }),
          params
        );
      },
      async updateDomain(
        organizationId: string,
        params: OrganizationSiteProfileDomainRequest
      ): Promise<OrganizationSiteProfileResponse> {
        return await api.post(
          replaceUrlParams(endpoints.sites.updateDomain, { organizationId }),
          params
        );
      },
      async updateHeroImage(
        organizationId: string,
        params: OrganizationSiteProfileHeroImageRequest
      ): Promise<OrganizationSiteProfileResponse> {
        return await api.postFile(
          replaceUrlParams(endpoints.sites.updateHeroImage, { organizationId }),
          params
        );
      },
      async updateLogoImage(
        organizationId: string,
        params: OrganizationSiteProfileLogoImageRequest
      ): Promise<OrganizationSiteProfileResponse> {
        return await api.postFile(
          replaceUrlParams(endpoints.sites.updateLogoImage, { organizationId }),
          params
        );
      },
      async updateSocials(
        organizationId: string,
        params: OrganizationSiteSocialsRequest
      ): Promise<OrganizationSiteProfileResponse> {
        return await api.post(
          replaceUrlParams(endpoints.sites.updateSocials, { organizationId }),
          params
        );
      },
    };
  }, [api]);
};

export const organizationSiteApiKeys = {
  get: (organizationId: string) => ['organizationSite', 'get', organizationId],
  checkDomainAvailability: (organizationId: string) => [
    'organizationSite',
    'checkDomainAvailability',
    organizationId,
  ],
  getProductGroups: (organizationId: string) => [
    'organizationSite',
    'getProductGroups',
    organizationId,
  ],
};
